import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import "@/App.css";
import { 
  Menu, 
  X, 
  ArrowRight, 
  Check, 
  Zap, 
  Target, 
  MessageSquare, 
  Pause,
  Clock,
  DollarSign,
  RefreshCw,
  Calendar,
  TrendingDown,
  ChevronRight,
  ExternalLink,
  Award,
  Mail,
  Cpu,
  CircuitBoard,
  Star,
  Briefcase,
  Play
} from "lucide-react";
import { Button } from "./components/ui/button";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/6ky36pij_logo.PNG";

// Booking URL - update this manually
const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/2Sa395sv0O6JZE7GBfpf";

// Circuit Z Logo Component
const CircuitZLogo = ({ className = "", size = "48" }) => (
  <img 
    src={LOGO_URL}
    alt="Zahar Lab Logo"
    className={className}
    style={{ width: size + 'px', height: size + 'px', objectFit: 'contain' }}
  />
);

// Founder Photo URL
const FOUNDER_PHOTO_URL = "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/hlmhz1ah_DSC01489%20-%20Edited.png";

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', href: '#services', homeOnly: true },
    { name: 'How It Works', href: '#how-it-works', homeOnly: true },
    { name: 'Pricing', href: '#pricing', homeOnly: true },
    { name: 'Portfolio', href: '/portfolio', isRoute: true },
    { name: 'Free Audit', href: 'https://audit.zaharlab.com/', external: true },
    { name: 'Free Funnel Guide', href: 'https://guide.zaharlab.com', external: true },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (link) => {
    if (link.homeOnly && !isHomePage) {
      window.location.href = '/' + link.href;
    } else if (link.href.startsWith('#')) {
      scrollToSection(link.href);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        data-testid="navbar"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/5' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <CircuitZLogo size="44" />
              <div className="hidden sm:flex flex-col">
                <span className="font-oxanium font-bold text-lg tracking-wider text-[#F0F4FF] group-hover:text-[#00D4FF] transition-colors" style={{ fontFamily: 'Oxanium, sans-serif' }}>
                  ZAHAR LAB
                </span>
                <span className="text-xs text-[#64748B] tracking-widest uppercase">Digital Marketing</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-[#94A3B8] hover:text-[#00D4FF] transition-colors tracking-wide uppercase"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                ) : link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-sm font-medium text-[#94A3B8] hover:text-[#00D4FF] transition-colors tracking-wide uppercase"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    className="text-sm font-medium text-[#94A3B8] hover:text-[#00D4FF] transition-colors tracking-wide uppercase"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </button>
                )
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <a
                href={isHomePage ? "#pricing" : "/#pricing"}
                onClick={(e) => { 
                  if (isHomePage) {
                    e.preventDefault(); 
                    scrollToSection('#pricing'); 
                  }
                }}
                className="btn-primary px-6 py-3 text-sm inline-flex items-center gap-2"
                data-testid="nav-cta-button"
              >
                Start Growing <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#F0F4FF]"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 p-2 text-[#F0F4FF]"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex flex-col items-center gap-6">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xl font-medium text-[#F0F4FF] hover:text-[#00D4FF] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xl font-medium text-[#F0F4FF] hover:text-[#00D4FF] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link)}
                  className="text-xl font-medium text-[#F0F4FF] hover:text-[#00D4FF] transition-colors"
                >
                  {link.name}
                </button>
              )
            ))}
            <a
              href={isHomePage ? "#pricing" : "/#pricing"}
              onClick={(e) => { 
                if (isHomePage) {
                  e.preventDefault(); 
                  scrollToSection('#pricing'); 
                }
                setIsMobileMenuOpen(false);
              }}
              className="btn-primary px-8 py-4 text-base mt-4"
            >
              Start Growing
            </a>
          </div>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center section-dark noise-overlay" data-testid="hero-section">
      {/* Animated Background */}
      <div className="hero-bg">
        <div className="circuit-nodes"></div>
        <div className="grid-lines"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 badge-accepting mb-8" data-testid="accepting-badge">
          <span className="pulse-dot"></span>
          <span>Now Accepting New Clients</span>
        </div>

        {/* Main Headline */}
        <h1 
          className="hero-title text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
          data-testid="hero-headline"
        >
          <span className="text-[#F0F4FF]">Stop Managing Freelancers.</span>
          <br />
          <span className="gradient-text">Start Scaling.</span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg sm:text-xl text-[#94A3B8] max-w-3xl mx-auto mb-10 leading-relaxed" data-testid="hero-subheadline">
          A high-powered, AI-enhanced growth partner who builds your content, websites, and ads — hands-on, fast, and accountable. No bloated teams. No excuses.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#pricing"
            onClick={(e) => { e.preventDefault(); document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="btn-primary btn-skewed px-8 py-4 text-base inline-flex items-center gap-2"
            data-testid="hero-cta-primary"
          >
            <span>View Pricing Plans</span>
          </a>
          <a
            href="https://audit.zaharlab.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost px-8 py-4 text-base inline-flex items-center gap-2"
            data-testid="hero-cta-secondary"
          >
            Get Your Free 5-Min Audit <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#080C14] to-transparent"></div>
    </section>
  );
};

// Problem Agitation Section
const ProblemSection = () => {
  const problems = [
    { icon: Clock, text: "Freelancers who miss deadlines" },
    { icon: DollarSign, text: "Agencies charging $5K+ for junior work" },
    { icon: RefreshCw, text: "Re-explaining your brand every single time" },
    { icon: Calendar, text: "Endless discovery calls that go nowhere" },
    { icon: TrendingDown, text: "Campaigns that launched late — or never" },
  ];

  return (
    <section id="problems" className="section-darker py-24 md:py-32 noise-overlay" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
          data-testid="problem-title"
        >
          <span className="text-[#F0F4FF]">Still Dealing</span>{' '}
          <span className="gradient-text">With This?</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {problems.map((problem, index) => (
            <div 
              key={index}
              className="problem-card circuit-card p-6 text-center group cursor-default"
              data-testid={`problem-card-${index}`}
            >
              <div className="problem-icon mx-auto mb-4">
                <problem.icon className="w-6 h-6" />
              </div>
              <p className="text-[#94A3B8] group-hover:text-[#F0F4FF] transition-colors text-sm">
                {problem.text}
              </p>
            </div>
          ))}
        </div>

        <p className="text-center text-xl text-[#00D4FF] mt-12 font-medium" style={{ fontFamily: 'Oxanium, sans-serif' }}>
          There's a better way.
        </p>
      </div>
    </section>
  );
};

// Solution/About Section
const SolutionSection = () => {
  return (
    <section id="about" className="section-dark py-24 md:py-32 circuit-pattern noise-overlay" data-testid="solution-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-8"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">I'm not an agency.</span>
          <br />
          <span className="gradient-text">I'm your relentless growth partner.</span>
        </h2>

        <p className="text-lg text-[#94A3B8] text-center max-w-3xl mx-auto mb-12 leading-relaxed">
          I traded the corporate grind for outcomes-driven work. Full-stack execution: design, copy, code, ads — all powered by AI and accountability. One person. Zero excuses.
        </p>

        {/* Photo + Terminal Quote Card Container */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10 max-w-4xl mx-auto">
          {/* Founder Photo */}
          <div 
            className="relative flex-shrink-0 w-[200px] h-[240px] md:w-[280px] md:h-[340px] rounded-xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #00D4FF 0%, #0057FF 100%)',
              padding: '2px',
              boxShadow: '0 0 20px rgba(0, 212, 255, 0.2), 0 0 40px rgba(0, 87, 255, 0.1)'
            }}
            data-testid="founder-photo"
          >
            <div className="relative w-full h-full rounded-[10px] overflow-hidden bg-[#0A0A0A]">
              <img 
                src={FOUNDER_PHOTO_URL}
                alt="Karim - Founder of Zahar Lab"
                className="w-full h-full object-cover object-top"
              />
              {/* Cinematic bottom gradient overlay */}
              <div 
                className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none"
                style={{
                  background: 'linear-gradient(to top, rgba(10, 10, 10, 0.6) 0%, transparent 100%)'
                }}
              />
            </div>
          </div>

          {/* Terminal Quote Card */}
          <div className="terminal-receipt p-6 md:p-10 flex-1" data-testid="quote-card">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="ml-4 text-xs text-[#64748B] uppercase tracking-widest">terminal</span>
            </div>
            <blockquote className="text-base md:text-lg lg:text-xl text-[#F0F4FF] leading-relaxed italic">
              "I'm building my reputation one win at a time. That means I work harder, care more, and move faster than any big agency ever could. 
              <span className="gradient-text font-semibold not-italic"> Your growth is the only metric that matters to me.</span>"
            </blockquote>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-12">
          <a 
            href="https://www.coursera.org/account/accomplishments/professional-cert/ZSCQXPZ6L6OU"
            target="_blank"
            rel="noopener noreferrer"
            className="cert-badge"
            data-testid="cert-badge"
          >
            <Award className="w-4 h-4" />
            Google Digital Marketing Certified
          </a>
          <div className="cert-badge">
            <Cpu className="w-4 h-4" />
            AI-Powered Workflows
          </div>
          <div className="cert-badge">
            <CircuitBoard className="w-4 h-4" />
            Full-Stack Execution
          </div>
        </div>
      </div>
    </section>
  );
};

// Value Stack Section
const ValueStackSection = () => {
  const valueItems = [
    { item: "Request-based workflow — one task at a time, delivered fast", price: "($∞)" },
    { item: "Comprehensive Growth Strategy", price: "($2,000)", crossed: true },
    { item: "Dedicated Notion Project Board", price: "" },
    { item: "48–72h Average Turnaround", price: "(priceless)" },
    { item: "Ad Campaigns, Newsletters, Video Edits", price: "($1,500)", crossed: true },
    { item: "Landing Pages, Funnels, Lead Magnets", price: "($2,500)", crossed: true },
    { item: "Blog Posts, Sales Pages, Websites", price: "($3,000)", crossed: true },
    { item: "SMS & Email Marketing", price: "($800)", crossed: true },
    { item: "AI Phone Receptionist", price: "($500)", crossed: true },
    { item: "Brand Templates & Design Assets", price: "($1,200)", crossed: true },
    { item: "Marketing Audit + Gap Analysis", price: "($750)", crossed: true },
    { item: "Automations & Integrations", price: "($600)", crossed: true },
    { item: "Full CRM — FREE", price: "($97/mo value)", crossed: true },
  ];

  return (
    <section id="value" className="section-darker py-24 md:py-32 noise-overlay" data-testid="value-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="gradient-text">Everything You Get</span>
        </h2>
        <p className="text-center text-[#94A3B8] mb-12">One flat rate. Unlimited value.</p>

        {/* Terminal Receipt */}
        <div className="terminal-receipt p-6 md:p-10" data-testid="value-receipt">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="ml-4 text-xs text-[#64748B] uppercase tracking-widest">value_stack.exe</span>
          </div>

          <div className="space-y-0">
            {valueItems.map((item, index) => (
              <div key={index} className="value-item">
                <span className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#00D4FF] flex-shrink-0" />
                  <span className="text-[#F0F4FF] text-sm md:text-base">{item.item}</span>
                </span>
                {item.price && (
                  <span className={`text-sm ${item.crossed ? 'line-through text-[#64748B]' : 'text-[#94A3B8]'}`}>
                    {item.price}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="border-t-2 border-dashed border-[#00D4FF]/30 mt-8 pt-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-[#64748B]">Total Value:</span>
              <span className="text-[#64748B] line-through">$10,000+/mo</span>
            </div>
            <p className="text-center text-[#00D4FF] text-sm">Multiple tiers available • Cancel anytime</p>
          </div>
        </div>

        <div className="text-center mt-10">
          <a
            href="#pricing"
            onClick={(e) => { e.preventDefault(); document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="btn-primary btn-skewed px-10 py-5 text-lg inline-flex items-center gap-3"
            data-testid="value-cta"
          >
            <span>View All Plans</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Submit",
      description: "Reach out however feels easiest for you."
    },
    {
      number: "02", 
      title: "Build",
      description: "I get to work. You review. We revise until perfect."
    },
    {
      number: "03",
      title: "Scale",
      description: "Publish, launch, grow. Then do it again."
    }
  ];

  return (
    <section id="how-it-works" className="section-dark py-24 md:py-32 circuit-pattern noise-overlay" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">How It</span>{' '}
          <span className="gradient-text">Works</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
          {steps.map((step, index) => (
            <div key={index} className="relative" data-testid={`step-${index + 1}`}>
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#00D4FF] to-transparent"></div>
              )}
              
              <div className="circuit-card p-8 text-center relative z-10">
                <div className="text-5xl font-bold gradient-text mb-4" style={{ fontFamily: 'Oxanium, sans-serif' }}>
                  {step.number}
                </div>
                <h3 className="text-2xl font-bold text-[#F0F4FF] mb-3" style={{ fontFamily: 'Oxanium, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-[#94A3B8]">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xl text-[#00D4FF] mt-12 font-medium" style={{ fontFamily: 'Oxanium, sans-serif' }}>
          No meetings. No delays. Just results.
        </p>
      </div>
    </section>
  );
};

// Rules Section
const RulesSection = () => {
  const rules = [
    {
      icon: RefreshCw,
      title: "Unlimited Requests",
      description: "Stack your backlog. I crush them in order."
    },
    {
      icon: Target,
      title: "One at a Time",
      description: "Deep focus = perfect execution."
    },
    {
      icon: MessageSquare,
      title: "No Meetings Required",
      description: "Notion + SMS only. Unless you want one."
    },
    {
      icon: Pause,
      title: "Pause or Cancel Anytime",
      description: "Zero risk. Full control."
    }
  ];

  return (
    <section id="rules" className="section-darker py-24 md:py-32 noise-overlay" data-testid="rules-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">The Rules of</span>{' '}
          <span className="gradient-text">The Game</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {rules.map((rule, index) => (
            <div key={index} className="rule-card" data-testid={`rule-card-${index}`}>
              <div className="w-12 h-12 flex items-center justify-center mb-4 bg-[#00D4FF]/10 border border-[#00D4FF]/30">
                <rule.icon className="w-6 h-6 text-[#00D4FF]" />
              </div>
              <h3 className="text-xl font-bold text-[#F0F4FF] mb-2" style={{ fontFamily: 'Oxanium, sans-serif' }}>
                {rule.title}
              </h3>
              <p className="text-[#94A3B8] text-sm">{rule.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const serviceColumns = [
    {
      title: "1-2 Day Tasks",
      color: "#00D4FF",
      services: ["Ad Campaigns", "Newsletters", "Video Edits", "Social Posts", "Email Broadcasts", "AI Prompt Engineering"]
    },
    {
      title: "3-7 Day Projects",
      color: "#0077FF",
      services: ["Marketing Audits", "Landing Pages", "Funnels", "Blog Posts", "CRM Pipeline Setup", "Automation Workflows"]
    },
    {
      title: "7+ Day Foundations",
      color: "#0057FF",
      services: ["Full Websites", "Email Marketing System", "Sales Pages", "AI Voice Agent (Inbound)", "Full GHL Sub-Account Build", "N8N Automation System"]
    }
  ];

  return (
    <section id="services" className="section-dark py-24 md:py-32 circuit-pattern noise-overlay" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-16"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">Services</span>{' '}
          <span className="gradient-text">Menu</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {serviceColumns.map((column, colIndex) => (
            <div 
              key={colIndex} 
              className="service-card circuit-card p-8"
              style={{ borderTopColor: column.color, borderTopWidth: '3px' }}
              data-testid={`service-column-${colIndex}`}
            >
              <h3 
                className="text-xl font-bold mb-6"
                style={{ fontFamily: 'Oxanium, sans-serif', color: column.color }}
              >
                {column.title}
              </h3>
              <ul className="space-y-4">
                {column.services.map((service, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#F0F4FF]">
                    <ChevronRight className="w-4 h-4 text-[#00D4FF]" />
                    {service}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-center text-[#94A3B8] mt-10">
          <span className="text-[#00D4FF]">+ Bonuses:</span> Brand templates, automations, full marketing audit & gap analysis
        </p>
      </div>
    </section>
  );
};

// Lead Magnets Section
const LeadMagnetsSection = () => {
  return (
    <section id="lead-magnets" className="section-darker py-24 md:py-32 noise-overlay" data-testid="lead-magnets-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-6"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">Free</span>{' '}
          <span className="gradient-text">Resources</span>
        </h2>
        <p className="text-center text-[#94A3B8] mb-12 max-w-2xl mx-auto">
          Not sure where to start? Let me show you exactly what's broken — free, no strings attached.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Free Audit Card */}
          <a
            href="https://audit.zaharlab.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="lead-magnet-card p-8 md:p-10 group"
            data-testid="lead-magnet-audit"
          >
            <div className="w-16 h-16 flex items-center justify-center mb-6 bg-[#00D4FF]/10 border border-[#00D4FF]/30 group-hover:bg-[#00D4FF]/20 transition-colors">
              <Target className="w-8 h-8 text-[#00D4FF]" />
            </div>
            <h3 className="text-2xl font-bold text-[#F0F4FF] mb-3" style={{ fontFamily: 'Oxanium, sans-serif' }}>
              Free 5-Minute Marketing Audit
            </h3>
            <p className="text-[#94A3B8] mb-6">
              Get a personalized breakdown of what's working, what's not, and where to focus next.
            </p>
            <span className="inline-flex items-center gap-2 text-[#00D4FF] font-semibold group-hover:gap-3 transition-all">
              Get My Free Audit <ArrowRight className="w-5 h-5" />
            </span>
          </a>

          {/* Funnel Guide Card */}
          <a
            href="https://guide.zaharlab.com"
            target="_blank"
            rel="noopener noreferrer"
            className="lead-magnet-card p-8 md:p-10 group"
            data-testid="lead-magnet-funnel"
          >
            <div className="w-16 h-16 flex items-center justify-center mb-6 bg-[#0057FF]/10 border border-[#0057FF]/30 group-hover:bg-[#0057FF]/20 transition-colors">
              <Zap className="w-8 h-8 text-[#0057FF]" />
            </div>
            <h3 className="text-2xl font-bold text-[#F0F4FF] mb-3" style={{ fontFamily: 'Oxanium, sans-serif' }}>
              Free Interactive Funnel Guide
            </h3>
            <p className="text-[#94A3B8] mb-6">
              Discover how a complete, high-converting funnel turns visitors into loyal, repeat customers.
            </p>
            <span className="inline-flex items-center gap-2 text-[#0057FF] font-semibold group-hover:gap-3 transition-all">
              Get Instant Access Now <ArrowRight className="w-5 h-5" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

// NEW Pricing Section with 4 Tiers
const PricingSection = () => {
  const pricingTiers = [
    {
      name: "Digital Foundation",
      price: "$499",
      type: "Request driven",
      popular: false,
      features: [
        "Website build (up to 5 pages) — mobile-first, conversion-focused design",
        "GA4 / GTM / Console setup",
        "Lead capture form",
        "Automated text & email confirmations",
        "Basic content creation (SEO blog posts, basic email broadcast, basic images, basic video editing)",
        "Basic ad campaign setup"
      ]
    },
    {
      name: "Growth System",
      price: "$1,499",
      type: "Request driven",
      popular: true,
      features: [
        "Everything in Digital Foundation, plus:",
        "Lead magnet funnel build (landing page + thank-you page)",
        "Booking page linked directly to calendar",
        "Email + SMS automation sequences",
        "CRM setup & pipeline configuration (GoHighLevel full sub-account)",
        "Booking calendar + auto-reminders",
        "Basic ad campaign (Meta or Google)",
        "Monthly performance report",
        "Intermediate content creation (copywriting, carousels, Hormozi-style video editing, complex email campaign, automation development)",
        "GHL marketing platform (mobile app, call management & tracking, review campaign automation, reputation management)"
      ]
    },
    {
      name: "AI-Powered System",
      price: "$2,999",
      type: "Self-driven. Strategy. Copy.",
      popular: false,
      features: [
        "Everything in Growth System, plus:",
        "Comprehensive audit*",
        "Strategic action report*",
        "AI voice inbound agent 24/7 (bilingual FR/EN, call summaries logged to CRM & email)",
        "Custom N8N automation workflows",
        "AI-powered lead follow-up sequences",
        "Full CRM buildout + integrations",
        "Weekly KPI reporting",
        "Instagram comment & DM automation for lead capture",
        "UGC AI creation",
        "Priority support"
      ]
    },
    {
      name: "Fractional AI CMO",
      price: "$4,999",
      type: "Self-driven · Limited spots",
      popular: false,
      features: [
        "Everything in AI-Powered System, plus:",
        "Full marketing strategy ownership",
        "Ongoing funnel optimization",
        "Content creation & scheduling (social posts, email newsletters)",
        "Paid ad management (min. $1,000/mo ad budget required)",
        "Monthly strategy call (60 min)",
        "Dedicated Slack channel",
        "Quarterly business review",
        "Dedicated Telegram AI bot",
        "Dedicated social media manager",
        "Dedicated ad manager"
      ]
    }
  ];

  const addons = [
    {
      name: "AI Personal Assistant Setup",
      description: "Drafts emails in your voice, organizes files, manages your calendar, summarizes meetings, organizes your Notion workspace",
      price: "$1,199 one-time",
      maintenance: "$199/month",
      badge: "Available on all tiers"
    },
    {
      name: "Full Marketing Audit",
      description: "Comprehensive audit of your entire marketing presence — funnels, ads, content, CRM, and brand positioning — delivered with a full strategic action report",
      price: "$2,000 one-time",
      maintenance: null,
      badge: "Available on all tiers"
    }
  ];

  return (
    <section id="pricing" className="section-dark py-24 md:py-32 circuit-pattern noise-overlay" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-6"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">Choose Your</span>{' '}
          <span className="gradient-text">Growth Plan</span>
        </h2>
        <p className="text-center text-[#94A3B8] mb-16 max-w-2xl mx-auto">
          Flexible tiers designed to scale with your business. No contracts. Cancel anytime.
        </p>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-20">
          {pricingTiers.map((tier, index) => (
            <div 
              key={index}
              className={`pricing-tier-card relative bg-[#0C1220] p-6 lg:p-8 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,212,255,0.15)] ${
                tier.popular 
                  ? 'border-2 border-[#00D4FF] shadow-[0_0_20px_rgba(0,212,255,0.2)]' 
                  : 'border border-[#00D4FF]/10 hover:border-[#00D4FF]/30'
              }`}
              data-testid={`pricing-tier-${index}`}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 bg-gradient-to-r from-[#00D4FF] to-[#0057FF] text-white text-xs font-bold uppercase tracking-wider">
                    <Star className="w-3 h-3" /> Most Popular
                  </span>
                </div>
              )}

              {/* Tier Name */}
              <h3 
                className="text-xl font-bold text-[#F0F4FF] mb-2 mt-2"
                style={{ fontFamily: 'Oxanium, sans-serif' }}
              >
                {tier.name}
              </h3>

              {/* Type Badge */}
              <span className="inline-block text-xs text-[#94A3B8] uppercase tracking-wider mb-4">
                {tier.type}
              </span>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Oxanium, sans-serif' }}>
                  {tier.price}
                </span>
                <span className="text-[#94A3B8]">/month</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[#94A3B8]">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full py-4 text-center font-bold uppercase tracking-wider transition-all duration-300 inline-block ${
                  tier.popular 
                    ? 'btn-primary' 
                    : 'bg-transparent border border-[#00D4FF]/50 text-[#00D4FF] hover:bg-[#00D4FF]/10 hover:border-[#00D4FF]'
                }`}
                data-testid={`pricing-cta-${index}`}
              >
                Book a Call
              </a>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="mt-20">
          <h3 
            className="text-2xl md:text-3xl font-bold text-center mb-4"
            style={{ fontFamily: 'Oxanium, sans-serif' }}
          >
            <span className="text-[#F0F4FF]">Add-ons —</span>{' '}
            <span className="gradient-text">available on any tier</span>
          </h3>
          <p className="text-center text-[#94A3B8] mb-10">Enhance your plan with powerful extras</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {addons.map((addon, index) => (
              <div 
                key={index}
                className="addon-card bg-[#0C1220] border border-[#00D4FF]/10 p-6 lg:p-8 hover:border-[#00D4FF]/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,255,0.1)]"
                data-testid={`addon-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h4 
                    className="text-lg font-bold text-[#F0F4FF]"
                    style={{ fontFamily: 'Oxanium, sans-serif' }}
                  >
                    {addon.name}
                  </h4>
                  <span className="text-xs px-2 py-1 bg-[#00D4FF]/10 text-[#00D4FF] uppercase tracking-wider">
                    {addon.badge}
                  </span>
                </div>
                <p className="text-[#94A3B8] text-sm mb-4">{addon.description}</p>
                <div className="flex flex-wrap gap-3">
                  <span className="text-lg font-bold gradient-text">{addon.price}</span>
                  {addon.maintenance && (
                    <span className="text-[#94A3B8] text-sm self-center">+ {addon.maintenance} maintenance</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Note */}
        <p className="text-center text-[#64748B] text-sm mt-12">
          All plans include a dedicated Notion workspace. No long-term contracts.
        </p>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  return (
    <section id="booking" className="section-darker py-24 md:py-32 noise-overlay" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 
          className="section-title text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4"
          style={{ fontFamily: 'Oxanium, sans-serif' }}
        >
          <span className="text-[#F0F4FF]">Not ready to commit?</span>{' '}
          <span className="gradient-text">Let's talk.</span>
        </h2>
        <p className="text-center text-[#94A3B8] mb-12 max-w-2xl mx-auto">
          Book a free 30-min strategy call — zero pressure, full value.
        </p>

        <div className="calendar-embed" data-testid="calendar-embed">
          <iframe
            src={BOOKING_URL}
            style={{ width: '100%', minHeight: '800px', height: '100%', border: 'none' }}
            scrolling="yes"
            title="Book a Strategy Call"
          />
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const navLinks = [
    { name: 'Services', href: '#services', homeOnly: true },
    { name: 'How It Works', href: '#how-it-works', homeOnly: true },
    { name: 'Pricing', href: '#pricing', homeOnly: true },
    { name: 'Portfolio', href: '/portfolio', isRoute: true },
    { name: 'Free Audit', href: 'https://audit.zaharlab.com/', external: true },
    { name: 'Funnel Guide', href: 'https://guide.zaharlab.com', external: true },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="section-dark py-24 relative overflow-hidden" data-testid="footer">
      {/* Watermark */}
      <div className="footer-watermark">ZAHAR</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Logo and Tagline */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <CircuitZLogo size="56" />
            <span className="font-bold text-2xl text-[#F0F4FF]" style={{ fontFamily: 'Oxanium, sans-serif' }}>
              ZAHAR LAB
            </span>
          </div>
          <p className="text-[#94A3B8] max-w-md mx-auto">
            Your relentless growth partner. No fluff. Just results.
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex flex-wrap items-center justify-center gap-6 mb-12">
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#94A3B8] hover:text-[#00D4FF] transition-colors"
              >
                {link.name}
              </a>
            ) : link.isRoute ? (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm text-[#94A3B8] hover:text-[#00D4FF] transition-colors"
              >
                {link.name}
              </Link>
            ) : (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-sm text-[#94A3B8] hover:text-[#00D4FF] transition-colors"
              >
                {link.name}
              </button>
            )
          ))}
        </nav>

        {/* Contact */}
        <div className="text-center mb-12">
          <a 
            href="mailto:karim@zaharlab.com"
            className="inline-flex items-center gap-2 text-[#00D4FF] hover:text-[#F0F4FF] transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-4 h-4" />
            karim@zaharlab.com
          </a>
        </div>

        {/* Certification */}
        <div className="flex justify-center mb-12">
          <a 
            href="https://www.coursera.org/account/accomplishments/professional-cert/ZSCQXPZ6L6OU"
            target="_blank"
            rel="noopener noreferrer"
            className="cert-badge"
            data-testid="footer-cert"
          >
            <Award className="w-4 h-4" />
            Google Digital Marketing & E-commerce Certificate
          </a>
        </div>

        {/* Cancel Anytime */}
        <div className="text-center">
          <span className="text-[#2E4A6B] text-sm uppercase tracking-[0.3em]" style={{ fontFamily: 'Oxanium, sans-serif' }}>
            — Cancel Anytime —
          </span>
        </div>

        {/* Copyright */}
        <div className="text-center mt-8 pt-8 border-t border-white/5">
          <p className="text-[#64748B] text-sm">
            © {new Date().getFullYear()} Zahar Lab. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Portfolio Page
const PortfolioPage = () => {
  const caseStudies = [
    {
      title: "AI Lead Generation Funnel",
      client: "Real Estate Service Business — Montreal",
      problem: "Leads were coming in manually, follow-up was inconsistent, and booking required constant back-and-forth.",
      built: [
        "High-converting lead magnet landing page",
        "Automated email + SMS sequence triggered instantly on form submission",
        "CRM contact creation and pipeline stage assignment — zero manual input"
      ],
      result: "Full funnel live in under a week. Every new lead gets followed up instantly, automatically, every time.",
      tags: ["GoHighLevel", "SMS Automation", "Email Sequences"],
      loomEmbedId: "6cb95751c6094318ba5152e3a9385512"
    },
    {
      title: "Personal Brand Website & Booking System",
      client: "Barber Shop & Training Academy — Montreal",
      problem: "A skilled barber and certified trainer with no digital presence — losing clients and students to competitors with a stronger online brand.",
      built: [
        "Premium personal brand website with luxury-inspired UI and mobile-first design",
        "Dual conversion structure — one flow for booking haircut appointments, one for barber certification inquiries",
        "Strong visual identity with clean typography, smooth interactions, and portfolio showcase for haircut work"
      ],
      result: "A polished, professional digital presence positioning Barber Krystal as a recognized brand in both grooming and barber training.",
      tags: ["Emergent", "Conversion-Focused UI", "Mobile-First Design"],
      loomEmbedId: "43a58eafb9a64c1f844de80e5e501614"
    },
    {
      title: "Full-Stack Real Estate Marketing System",
      client: "Real Estate Agent — Montreal",
      problem: "Leads were slipping through the cracks. No automated follow-up, no consistent nurturing, and manual processes eating up hours every week.",
      built: [
        "High-converting lead magnet with irresistible value proposition and automated instant delivery",
        "Custom QR code integration for offline-to-online lead capture",
        "Complete SMS + email automation sequences with personalized follow-ups",
        "Automated calendar booking system with confirmation and reminder workflows",
        "Social media comment & DM automation for lead capture at scale"
      ],
      result: "A fully automated lead generation machine — from first touch to booked appointment — running 24/7 without lifting a finger.",
      tags: ["GoHighLevel", "Lead Magnet", "Marketing Automation", "QR Codes"],
      showcaseImage: "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/32f1q7sg_1%3AREAL%20ESTATE%20funnel%20%26%20AUTOMATIONS%20.png"
    },
    {
      title: "Multi-Page Restaurant Website",
      client: "Authentic Neapolitan Pizzeria — New York",
      problem: "A beloved local pizza shop with zero online presence — losing customers to competitors who made it easy to browse menus and order online.",
      built: [
        "Stunning multi-page website showcasing authentic Neapolitan heritage and premium ingredients",
        "Complete online ordering system with menu categories, customization options, and cart functionality",
        "Table reservation system integrated directly into the site",
        "Location pages with maps, hours, and contact information",
        "Mobile-optimized design for on-the-go ordering"
      ],
      result: "A digital storefront that captures the warmth of Naples — driving online orders and reservations while building brand loyalty.",
      tags: ["Web Design", "E-commerce", "Restaurant", "Mobile-First"],
      showcaseImage: "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/yrjozk4n_2-New%20York%20Pizza%20Shop.png"
    },
    {
      title: "Digital Product Funnel — Dating & Relationships",
      client: "Relationship Coach & Author",
      problem: "Valuable coaching expertise with no way to reach a wider audience or generate leads at scale beyond word-of-mouth referrals.",
      built: [
        "Professional e-book product: 'Love Like a CEO: Core Dating Blueprint' — positioned as an irresistible entry point",
        "High-converting sales page with compelling copy and social proof",
        "Seamless checkout and instant digital delivery system",
        "Strategic tripwire funnel designed to convert readers into coaching clients",
        "Email sequence nurturing buyers toward premium services"
      ],
      result: "A scalable digital product ecosystem turning cold traffic into paying customers — and paying customers into high-ticket coaching clients.",
      tags: ["Digital Products", "Sales Funnel", "E-book", "Tripwire Strategy"],
      showcaseImage: "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/chaffkp1_3%3Adigital%20e-book%20funnel.png"
    },
    {
      title: "Digital Product Funnel — Psychology & Self-Defense",
      client: "Psychology Expert & Coach",
      problem: "Deep expertise in spotting manipulation and psychological tactics — but no scalable way to monetize knowledge or attract coaching clients.",
      built: [
        "Premium e-book product: 'Cold Eyes, Warm Mind: 18 Psychological Laws to Outsmart Manipulators' — striking design with powerful positioning",
        "Conversion-optimized sales page with pattern-interrupt copy",
        "Frictionless checkout with instant PDF delivery",
        "Tripwire funnel architecture leading to high-end coaching upsells",
        "Automated email sequences building trust and driving conversions"
      ],
      result: "A compelling digital product that establishes authority, generates revenue on autopilot, and feeds a pipeline of qualified coaching leads.",
      tags: ["Digital Products", "Sales Funnel", "E-book", "Psychology"],
      showcaseImage: "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/kqmbu60g_3%3ADigital%20ebook%20funnel.png"
    },
    {
      title: "Interactive Smile Preview Software",
      client: "Orthodontic Clinics — SaaS Product",
      problem: "Orthodontists struggle to convert consultations into paying patients. Prospects leave undecided, and clinics have no way to capture leads at scale or demonstrate results before treatment begins.",
      built: [
        "Interactive 'See Your Future Smile' tool — patients upload a photo and preview their transformed smile in seconds",
        "Frictionless lead capture built into the experience — contact info collected before revealing results",
        "White-labeled solution for clinics to embed on their websites or use in-office",
        "Automated lead delivery to clinic CRM with patient details and smile preview attached",
        "Mobile-optimized interface designed for waiting room tablets and social media traffic"
      ],
      result: "The ultimate patient acquisition machine — turning curiosity into consultations by letting prospects visualize their dream smile before they ever sit in the chair.",
      tags: ["SaaS", "Lead Generation", "Healthcare", "Interactive Tool"],
      showcaseImage: "https://customer-assets.emergentagent.com/job_relentless-marketing/artifacts/ecpad8h2_orthodontist%20softaware.png"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 section-dark noise-overlay">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: 'Oxanium, sans-serif' }}
            data-testid="portfolio-headline"
          >
            <span className="text-[#F0F4FF]">Our</span>{' '}
            <span className="gradient-text">Work</span>
          </h1>
          <p className="text-lg md:text-xl text-[#94A3B8] max-w-2xl mx-auto">
            Real systems. Real results. Built from scratch.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 md:py-24 section-darker noise-overlay">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div 
                key={index}
                className="case-study-card bg-[#0C1220] border border-[#00D4FF]/15 p-6 md:p-10 transition-all duration-300 hover:border-[#00D4FF]/40 hover:shadow-[0_0_30px_rgba(0,212,255,0.1)]"
                data-testid={`case-study-${index}`}
              >
              {/* Video - Loom Embed or Placeholder or Showcase Image */}
                {study.loomEmbedId ? (
                  <div 
                    className="aspect-video bg-[#080C14] border border-[#00D4FF]/10 mb-8 overflow-hidden"
                    style={{ position: 'relative', paddingBottom: '64.86%', height: 0 }}
                  >
                    <iframe 
                      src={`https://www.loom.com/embed/${study.loomEmbedId}`}
                      frameBorder="0"
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      title={`${study.title} video`}
                    />
                  </div>
                ) : study.showcaseImage ? (
                  <div className="mb-8 overflow-hidden border border-[#00D4FF]/10 hover:border-[#00D4FF]/30 transition-all">
                    <img 
                      src={study.showcaseImage}
                      alt={`${study.title} showcase`}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ) : study.videoPlaceholder && (
                  <div className="aspect-video bg-[#080C14] border border-[#00D4FF]/10 mb-8 flex items-center justify-center group cursor-pointer hover:border-[#00D4FF]/30 transition-all">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center group-hover:bg-[#00D4FF]/20 transition-colors">
                        <Play className="w-6 h-6 text-[#00D4FF] ml-1" />
                      </div>
                      <p className="text-[#64748B] text-sm">Loom video coming soon</p>
                    </div>
                  </div>
                )}

                {/* Client Badge */}
                <span className="inline-block text-xs text-[#00D4FF] uppercase tracking-widest mb-3">
                  {study.client}
                </span>

                {/* Title */}
                <h2 
                  className="text-2xl md:text-3xl font-bold text-[#F0F4FF] mb-6"
                  style={{ fontFamily: 'Oxanium, sans-serif' }}
                >
                  {study.title}
                </h2>

                {/* Problem */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-2">The Problem</h3>
                  <p className="text-[#F0F4FF]">{study.problem}</p>
                </div>

                {/* What Was Built */}
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-3">What Was Built</h3>
                  <ul className="space-y-2">
                    {study.built.map((item, bIndex) => (
                      <li key={bIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                        <span className="text-[#F0F4FF]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Result */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-2">The Result</h3>
                  <p className="text-lg gradient-text font-medium">{study.result}</p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {study.tags.map((tag, tIndex) => (
                    <span 
                      key={tIndex}
                      className="px-3 py-1 text-xs bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 md:py-28 section-dark circuit-pattern noise-overlay">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Oxanium, sans-serif' }}
          >
            <span className="text-[#F0F4FF]">Want a system</span>{' '}
            <span className="gradient-text">like this?</span>
          </h2>
          <p className="text-[#94A3B8] mb-10 text-lg">
            Book a free 20-minute call and I'll show you exactly what I'd build for your business.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-10 py-5 text-lg inline-flex items-center gap-3"
            data-testid="portfolio-cta"
          >
            Book a Call <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <ValueStackSection />
      <HowItWorksSection />
      <RulesSection />
      <ServicesSection />
      <LeadMagnetsSection />
      <PricingSection />
      <BookingSection />
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
